import React, { useState, useEffect, useRef } from 'react'
import { Sparkles, Columns, Layers, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../lib/utils'

export default function BeforeAfterSlider({
  before,
  after,
  title,
  description,
  beforeLabel = 'Før',
  afterLabel = 'Efter',
  heightClass = 'h-[320px] sm:h-[400px]'
}) {
  const [sliderPosition, setSliderPosition] = useState(50)
  const [isDragging, setIsDragging] = useState(false)
  const [viewMode, setViewMode] = useState('slider') // 'slider' | 'side-by-side'
  const [hasInteracted, setHasInteracted] = useState(false)
  const containerRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(0)

  // Update container width on resize
  useEffect(() => {
    if (!containerRef.current) return
    const updateWidth = () => {
      setContainerWidth(containerRef.current.getBoundingClientRect().width)
    }
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [viewMode])

  // Handle slider movement
  const handleMove = (clientX) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const x = clientX - rect.left
    const position = Math.max(0, Math.min(100, (x / rect.width) * 100))
    setSliderPosition(position)
    if (!hasInteracted) setHasInteracted(true)
  }

  const handleTouchMove = (e) => {
    if (!isDragging) return
    if (e.touches.length > 0) {
      handleMove(e.touches[0].clientX)
    }
  }

  const handleMouseMove = (e) => {
    if (!isDragging) return
    handleMove(e.clientX)
  }

  // Click on container to jump/move smoothly to that position
  const handleContainerClick = (e) => {
    if (isDragging || viewMode !== 'slider') return
    // If clicking a button, don't move slider
    if (e.target.closest('button') || e.target.closest('.slider-handle')) return
    handleMove(e.clientX)
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (viewMode !== 'slider') return
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      setSliderPosition((prev) => Math.max(0, prev - 5))
      setHasInteracted(true)
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      setSliderPosition((prev) => Math.min(100, prev + 5))
      setHasInteracted(true)
    }
  }

  // Drag end listeners
  useEffect(() => {
    const handleMouseUp = () => setIsDragging(false)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchend', handleMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchend', handleMouseUp)
    }
  }, [])

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-xl overflow-hidden flex flex-col h-full group/card hover:shadow-2xl hover:border-gray-200/80 transition-all duration-300">
      {/* Header Info */}
      <div className="p-6 flex-grow">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h3 className="text-lg sm:text-xl font-black text-gray-900 flex items-center gap-2">
            <span className="p-1.5 rounded-lg bg-[#01529E]/5 text-[#01529E]">
              <Sparkles className="h-4 w-4" />
            </span>
            {title}
          </h3>
          
          {/* Mode Selector */}
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl shrink-0">
            <button
              onClick={() => setViewMode('slider')}
              className={cn(
                "p-1.5 rounded-lg transition-all cursor-pointer",
                viewMode === 'slider' 
                  ? "bg-white text-[#01529E] shadow-sm" 
                  : "text-gray-500 hover:text-gray-900"
              )}
              title="Skyder-visning"
            >
              <Layers className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('side-by-side')}
              className={cn(
                "p-1.5 rounded-lg transition-all cursor-pointer",
                viewMode === 'side-by-side' 
                  ? "bg-white text-[#01529E] shadow-sm" 
                  : "text-gray-500 hover:text-gray-900"
              )}
              title="Side-om-side visning"
            >
              <Columns className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Image Container */}
      <div 
        className="relative w-full overflow-hidden select-none bg-gray-900 border-t border-gray-100"
        style={{ outline: 'none' }}
      >
        {viewMode === 'slider' ? (
          /* SLIDER VIEW */
          <div
            ref={containerRef}
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onMouseDown={() => {
              setIsDragging(true)
            }}
            onTouchStart={() => {
              setIsDragging(true)
            }}
            onMouseMove={handleMouseMove}
            onTouchMove={handleTouchMove}
            onClick={handleContainerClick}
            className={cn(
              "relative w-full overflow-hidden cursor-ew-resize focus:ring-2 focus:ring-[#01529E]/50 transition-shadow duration-200",
              heightClass
            )}
          >
            {/* After Image (Base) */}
            <img
              src={`${after}?cdn-cgi/image/width=800,quality=85,format=auto`}
              alt={`${title} - ${afterLabel}`}
              className="absolute inset-0 h-full w-full object-cover pointer-events-none"
              draggable="false"
            />
            
            {/* Before Image (Overlay) */}
            <div
              className="absolute inset-0 h-full overflow-hidden pointer-events-none"
              style={{ width: `${sliderPosition}%` }}
            >
              <img
                src={`${before}?cdn-cgi/image/width=800,quality=85,format=auto`}
                alt={`${title} - ${beforeLabel}`}
                className="absolute inset-0 h-full object-cover max-w-none pointer-events-none"
                style={{ width: containerWidth ? `${containerWidth}px` : '100%' }}
                draggable="false"
              />
            </div>

            {/* Glassmorphism Badges */}
            <div className="absolute left-4 top-4 z-10 pointer-events-none">
              <span className="backdrop-blur-md bg-black/40 text-white text-[11px] font-bold px-3 py-1.5 rounded-xl border border-white/10 tracking-wider uppercase shadow-sm">
                {beforeLabel}
              </span>
            </div>
            <div className="absolute right-4 top-4 z-10 pointer-events-none">
              <span className="backdrop-blur-md bg-emerald-950/60 text-emerald-200 text-[11px] font-bold px-3 py-1.5 rounded-xl border border-emerald-500/20 tracking-wider uppercase shadow-sm">
                {afterLabel}
              </span>
            </div>

            {/* Interactive Slider Handle */}
            <div
              className="slider-handle absolute top-0 bottom-0 w-1 bg-gradient-to-b from-white/20 via-white to-white/20 cursor-ew-resize z-20 shadow-[0_0_10px_rgba(255,255,255,0.4)]"
              style={{ left: `${sliderPosition}%` }}
            >
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white text-gray-900 shadow-2xl border border-gray-200/80 flex items-center justify-center transition-transform duration-200 hover:scale-110 active:scale-95">
                {!hasInteracted && (
                  <span className="absolute -inset-2 rounded-full bg-white/30 animate-ping pointer-events-none" />
                )}
                <div className="flex items-center justify-center text-gray-700">
                  <ChevronLeft className="h-4 w-4 -mr-1 shrink-0" />
                  <ChevronRight className="h-4 w-4 -ml-1 shrink-0" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* SIDE-BY-SIDE VIEW */
          <div className={cn("grid grid-cols-2 gap-0.5 bg-gray-200", heightClass)}>
            {/* Before Image */}
            <div className="relative h-full w-full overflow-hidden">
              <img
                src={`${before}?cdn-cgi/image/width=500,quality=85,format=auto`}
                alt={`${title} - ${beforeLabel}`}
                className="h-full w-full object-cover"
              />
              <div className="absolute left-3 top-3">
                <span className="backdrop-blur-md bg-black/50 text-white text-[10px] font-bold px-2.5 py-1 rounded-lg border border-white/10 tracking-wider uppercase shadow-sm">
                  {beforeLabel}
                </span>
              </div>
            </div>

            {/* After Image */}
            <div className="relative h-full w-full overflow-hidden">
              <img
                src={`${after}?cdn-cgi/image/width=500,quality=85,format=auto`}
                alt={`${title} - ${afterLabel}`}
                className="h-full w-full object-cover"
              />
              <div className="absolute right-3 top-3">
                <span className="backdrop-blur-md bg-emerald-950/70 text-emerald-200 text-[10px] font-bold px-2.5 py-1 rounded-lg border border-emerald-500/20 tracking-wider uppercase shadow-sm">
                  {afterLabel}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
